import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Tag } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fixImageUrl(url) {
  if (!url) return url;
  const currentApi = `${window.location.origin}/api`;
  return url
    .replace(/https?:\/\/[^/]*\.emergent\.host\/api/g, currentApi)
    .replace(/https?:\/\/[^/]*\.emergentagent\.com\/api/g, currentApi)
    .replace(/https?:\/\/[^/]*\.preview\.emergentagent\.com\/api/g, currentApi);
}

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((res) => {
      setPosts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  if (loading) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20 text-center">
          <p className="text-[#6B7280]">Loading posts...</p>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-list-page" className="pt-20">
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 lg:mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Insights & Guides
            </p>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-light text-[#0D2D54] tracking-tight mb-4">
              Insights
            </h1>
            <p className="text-base text-[#6B7280] max-w-lg mx-auto">
              Expert advice on virtual staging, property marketing and selling property faster in the UK.
            </p>
          </div>

          {posts.length === 0 ? (
            <p className="text-center text-[#6B7280]">No posts yet. Check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {posts.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${i}`}
                  className="group block border border-[#E8E8E4] hover:-translate-y-1 hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  {post.featured_image && (
                    <div className="overflow-hidden h-48">
                      <img
                        src={fixImageUrl(post.featured_image)}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-[#758C7B] font-medium uppercase tracking-[0.1em]">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#6B7280]">
                        <Clock size={12} />
                        {post.reading_time} min read
                      </span>
                    </div>
                    <h2 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-2 group-hover:text-[#1A3D6B] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-[#6B7280] leading-relaxed mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-[#0D2D54] group-hover:text-[#758C7B] transition-colors">
                      Read more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
